import { EventEmitter } from '@spotify-internal/emitter';
import { HarmonyEvent } from '../enums/harmony_event';
import { HarmonyErrors } from '../enums/errors';
import { PlayerQueueError } from '../error/player_queue';
const QID_SEPARATOR = ':::';
/**
 * Creates QueueItems from an array of ContextTracks.
 *
 * @param contextTracks - The ContextTracks to parse.
 * @param options - The options for the operation.
 * @param options.ignoreMetaForTrack - When set to true, meta items would
 * not be checked for building the track list, as in the case of repeating
 * contexts.
 * @return The parsed QueueItems.
 */
function createQueueItems(contextTracks, options = { ignoreMetaForTrack: false }) {
    var _a, _b, _c, _d;
    const items = [];
    const tracks = [];
    let hasQueuedTracks = false;
    let encounteredDelimiter = false;
    if (!(contextTracks === null || contextTracks === void 0 ? void 0 : contextTracks.length)) {
        return { items, tracks, hasQueuedTracks };
    }
    for (const contextTrack of contextTracks) {
        const itemType = contextTrack.uri === 'spotify:delimiter' ||
            /^spotify:meta:/.test(contextTrack.uri)
            ? 'meta'
            : 'track';
        const queueID = `${contextTrack.uid}${QID_SEPARATOR}${(_a = contextTrack.metadata) === null || _a === void 0 ? void 0 : _a.iteration}`;
        const item = {
            type: itemType,
            uri: contextTrack.uri,
            uid: contextTrack.uid,
            qid: queueID,
            metadata: contextTrack.metadata || {},
            hidden: ((_b = contextTrack.metadata) === null || _b === void 0 ? void 0 : _b.hidden) === 'true',
            queued: ((_c = contextTrack.metadata) === null || _c === void 0 ? void 0 : _c.is_queued) === 'true',
            provider: contextTrack.provider,
            removed_reasons: contextTrack.removed,
            $blck_rs: contextTrack.blocked,
        };
        if (item.queued) {
            hasQueuedTracks = true;
        }
        items.push(item);
        if (item.type === 'meta') {
            encounteredDelimiter = true;
        }
        else if ((options.ignoreMetaForTrack || !encounteredDelimiter) &&
            item.type === 'track' &&
            !item.hidden &&
            !((_d = item.removed_reasons) === null || _d === void 0 ? void 0 : _d.length)) {
            tracks.push(item);
        }
    }
    return { items, tracks, hasQueuedTracks };
}
/**
 * Creates ContextTracks from an array of QueueItems.
 *
 * @param items - The QueueItems to parse.
 * @param options - Options to set fields in the ContextTracks.
 * @param options.queued - Whether to set the items as queued.
 * @return The parsed ContextTracks.
 */
export function createContextTracks(items, options = {}) {
    if (!Array.isArray(items)) {
        throw new TypeError('Items is not an array.');
    }
    if (!items.length) {
        return [];
    }
    const tracks = [];
    for (let item of items) {
        if (!item) {
            continue;
        }
        if (typeof item === 'string') {
            item = { type: 'track', uri: item };
        }
        const track = {
            uri: item.uri,
            uid: item.uid,
            metadata: item.metadata || {},
            removed: item.removed_reasons,
            blocked: item.$blck_rs,
            provider: item.provider,
        };
        if (item.queued || options.queued) {
            track.metadata.is_queued = 'true';
            track.provider = 'queue';
        }
        if (item.hidden || item.type === 'meta') {
            track.metadata.hidden = 'true';
        }
        tracks.push(track);
    }
    return tracks;
}
export function queueTrackFromQid(qid) {
    const [uid, iteration] = qid.split(QID_SEPARATOR);
    const result = {
        type: 'track',
        uid,
    };
    if (iteration) {
        result.metadata = { iteration };
    }
    return result;
}
/**
 * Represents the Context Player Queue.
 */
export class QueueManager extends EventEmitter {
    constructor(options) {
        super();
        /**
         * A flag that indicates whether the instance has an initial state.
         *
         * Used to distinguish between a `_currentPlayerState` being null because the
         * state has not been fetched yet or because the state is really null.
         */
        this._hasInitialState = false;
        /**
         * Whether or not to show queues for inactive player states.
         */
        this._reportInactiveQueues = false;
        /**
         * Only report the queue if it's local.
         */
        this._onlyLocalQueue = false;
        /**
         * The last known Context Player State.
         */
        this._currentPlayerState = null;
        /**
         * The current InternalPlayerQueue based on the last Context Player State.
         */
        this._currentInternalQueue = null;
        /**
         * The current PlayerQueue.
         */
        this._currentQueue = null;
        /**
         * A helper flag that indicates whether are queued items in the queue.
         */
        this._hasQueuedTracks = false;
        this._connectClient = options.connectClient;
        this._runner = options.runner || ((action) => action());
        this._parsePlayerState = this._parsePlayerState.bind(this);
        this._onlyLocalQueue = !!options.onlyLocalQueue;
        this._reportInactiveQueues = !!options.reportInactiveQueues;
        this._connectClient.on("player_state_changed" /* CONNECT_API_PLAYER_STATE_CHANGED */, this._onPlayerStateChanged.bind(this));
        if ('initialContextPlayerState' in options) {
            this._parsePlayerState(options.initialContextPlayerState);
        }
    }
    static create(options) {
        return new QueueManager(options);
    }
    /**
     * Handles the PlayerStateChanged event from the Connect State client.
     *
     * @param e - The PlayerState changed event.
     */
    _onPlayerStateChanged(e) {
        const { playerState, orphaned, isLocal } = e.data;
        if ((this._onlyLocalQueue && !isLocal) ||
            (!this._reportInactiveQueues && orphaned)) {
            return;
        }
        this._parsePlayerState(playerState);
    }
    /**
     * Parses the ContextPlayerState into an InternalPlayerQueue.
     *
     * @param state - The ContextPlayerState to parse.
     * @return The parsed InternalPlayerQueue.
     */
    _parsePlayerState(state) {
        var _a, _b, _c;
        this._hasInitialState = true;
        this._currentPlayerState = state !== null && state !== void 0 ? state : null;
        if (state) {
            if (state.queue_revision === ((_a = this._currentInternalQueue) === null || _a === void 0 ? void 0 : _a.revision)) {
                // The queue is the same, so we don't want to process it again.
                return null;
            }
            this._hasQueuedTracks = false;
            const options = {
                ignoreMetaForTrack: (_b = state.options) === null || _b === void 0 ? void 0 : _b.repeating_context,
            };
            const revision = state.queue_revision;
            const currentTrack = createQueueItems([state.track])
                .items[0];
            const next = createQueueItems(state.next_tracks, options);
            const prev = createQueueItems(state.prev_tracks, options);
            this._hasQueuedTracks = next.hasQueuedTracks;
            const context = {
                uri: state.context_uri,
                metadata: (_c = state.context_metadata) !== null && _c !== void 0 ? _c : undefined,
            };
            this._currentInternalQueue = {
                revision,
                context,
                current_track: currentTrack,
                next_items: next.items,
                previous_items: prev.items,
            };
            this._currentQueue = {
                revision,
                context,
                current_track: currentTrack,
                next_tracks: next.tracks,
                previous_tracks: prev.tracks,
            };
        }
        else {
            this._hasQueuedTracks = false;
            this._currentInternalQueue = null;
            this._currentQueue = null;
        }
        this.emit(HarmonyEvent.PLAYER_QUEUE_CHANGED, {
            internalPlayerQueue: this._currentInternalQueue,
            playerQueue: this._currentQueue,
        });
        return this._currentInternalQueue;
    }
    /**
     * Sets the next_tracks of the queue.
     *
     * @param tracks - The tracks that will be set as the new value of the
     *   next_tracks array of the current ContextPlayerState.
     * @param deviceId - The target device id.
     * @return A Promise that will be resolved if the next_tracks was successfully
     *   set.
     */
    _setQueueNext(tracks, deviceId) {
        const currentPlayerState = this._currentPlayerState;
        if (!currentPlayerState) {
            return Promise.reject(new PlayerQueueError(HarmonyErrors.QUEUE_MANAGER_NO_STATE, 'Cannot perform operation; no current state.'));
        }
        const newState = {
            next_tracks: tracks,
            prev_tracks: currentPlayerState.prev_tracks,
            queue_revision: currentPlayerState.queue_revision,
        };
        return this._runner(() => {
            return this._connectClient.setQueue(newState, deviceId);
        }, deviceId);
    }
    /**
     * Inserts an array of tracks as queued.
     *
     * @param tracks - The items that would be added to queued.
     * @param deviceId - The target device id.
     * @return A Promise that will be resolved if the next_tracks was successfully
     * set.
     */
    _appendQueued(tracks, deviceId) {
        var _a, _b;
        const currentPlayerState = this._currentPlayerState;
        if (!currentPlayerState) {
            return Promise.reject(new PlayerQueueError(HarmonyErrors.QUEUE_MANAGER_NO_STATE, 'Cannot perform operation; no current state.'));
        }
        // Clone the current next tracks.
        const nextTracks = Array.from((_a = currentPlayerState.next_tracks) !== null && _a !== void 0 ? _a : []);
        if (!this._hasQueuedTracks) {
            nextTracks.push(...tracks);
        }
        else {
            let wasInserted = false;
            // Find the last queued item in the current list and append the tracks.
            for (let i = 0, l = nextTracks.length; i < l; i++) {
                const track = nextTracks[i];
                if (((_b = track.metadata) === null || _b === void 0 ? void 0 : _b.is_queued) === 'true') {
                    continue;
                }
                else {
                    nextTracks.splice(i, 0, ...tracks);
                    wasInserted = true;
                    break;
                }
            }
            if (!wasInserted) {
                // The queue only contained queued tracks, so the items were never
                // inserted, so append the items at the end of the list.
                nextTracks.push(...tracks);
            }
        }
        return this._setQueueNext(nextTracks, deviceId);
    }
    /**
     * Returns the current InternalPlayerQueue.
     *
     * @return A Promise that will be resolved with the current
     *   InternalPlayerQueue or null if there is no InternalPlayerQueue.
     */
    getInternalPlayerQueue() {
        if (!this._hasInitialState) {
            return this._connectClient
                .getContextPlayerState()
                .then(this._parsePlayerState);
        }
        return Promise.resolve(this._currentInternalQueue);
    }
    /**
     * Returns the current PlayerQueue.
     *
     * Unlike the InternalPlayerQueue , a PlayerQueue only contains tracks. Other
     * meta items like delimiters and placeholders are filtered out.
     *
     * Additionally, hidden items are filtered out by default (see options below).
     *
     * @return A Promise that will be resolved with the current PlayerQueue
     *   or null if there is no PlayerQueue.
     */
    getPlayerQueue() {
        if (!this._hasInitialState) {
            return this._connectClient
                .getContextPlayerState()
                .then(this._parsePlayerState)
                .then(() => this._currentQueue);
        }
        return Promise.resolve(this._currentQueue);
    }
    /**
     * Sets a new InternalPlayerQueue, replacing the `next_items` and
     * `previous_items` of the current InternalPlayerQueue.
     *
     * Note that while this function expects a full InternalPlayerQueue, the
     * `current_track` in the provided  InternalPlayerQueuei s always ignored,
     * as this function cannot be used to set the currently playing track.
     *
     * The `context` property is also ignored.
     *
     * @param internalQueue - The InternalPlayerQueue to set. The revision of this
     *   object will be used to check against the latest known revision. If
     *   the revisions match, the `next_items` and `previous_items` from the
     *   provided InternalPlayerQueue will be set as the new `next_items` and
     *   `previous_items`.
     * @param deviceId - The target device id.
     * @param options - Options for the operation.
     * @param options.ignoreRevision - When set to `true`, the revisions will not
     *   be compared. Note that this can lead to race conditions and undefined
     *   behaviour. Defaults to `false`.
     * @return A Promise that will be resolved with `true` if the operation
     *   succeeded or `false` otherwise.
     */
    setInternalPlayerQueue(internalQueue, deviceId, options = { ignoreRevision: false }) {
        if (!this._currentPlayerState) {
            return Promise.reject(new PlayerQueueError(HarmonyErrors.QUEUE_MANAGER_NO_STATE, 'Cannot perform operation; no current state.'));
        }
        if (internalQueue.revision !== this._currentPlayerState.queue_revision &&
            !options.ignoreRevision) {
            return Promise.reject(new PlayerQueueError(HarmonyErrors.QUEUE_OPERATION_STALE_REVISION, 'Cannot perform operation; revision is stale.'));
        }
        const newState = {
            next_tracks: createContextTracks(internalQueue.next_items),
            prev_tracks: createContextTracks(internalQueue.previous_items),
            queue_revision: options.ignoreRevision
                ? undefined
                : this._currentPlayerState.queue_revision,
        };
        return this._runner(() => {
            return this._connectClient.setQueue(newState, deviceId);
        }, deviceId);
    }
    /**
     * Queues tracks, adding them after any existing queued tracks.
     *
     * @param items - The items to queue.
     * @param deviceId - The target device id.
     * @return A Promise that will be resolved with `true` if the operation
     * succeeded or `false` otherwise.
     */
    addToQueue(items, deviceId) {
        if (!Array.isArray(items)) {
            return Promise.reject(new TypeError('Argument `items` must be an array.'));
        }
        const tracks = createContextTracks(items, { queued: true });
        if (!tracks.length) {
            return Promise.resolve(false);
        }
        if (tracks.length === 1) {
            // If we have a single track, we can use the add_to_queue endpoint, which
            // can only take a single track. This allows us to bypass having to do
            // a complex queue insertion.
            return this._runner(() => {
                return this._connectClient.addToQueue(tracks[0], deviceId);
            }, deviceId);
        }
        return this._appendQueued(tracks, deviceId);
    }
    /**
     * Removes a track with a given QID from the current iteration of the queue.
     *
     * Due to how the context player works, the track would only be removed in
     * the current loop of a particular context if the track is provided by the
     * context itself.
     *
     * @param item - A string `qid` of the track, or a PlayerQueueItem.
     * @param deviceId - The target device id.
     * @return A Promise that will be resolved with `true` if the operation
     * succeeded or `false` otherwise.
     */
    removeNext(item, deviceId) {
        var _a, _b, _c;
        const currentPlayerState = this._currentPlayerState;
        if (!currentPlayerState) {
            return Promise.reject(new PlayerQueueError(HarmonyErrors.QUEUE_MANAGER_NO_STATE, 'Cannot perform operation; no current state.'));
        }
        let comparator;
        if (typeof item === 'string') {
            comparator = queueTrackFromQid(item);
        }
        else {
            comparator = item;
        }
        const nextTracks = Array.from((_a = currentPlayerState.next_tracks) !== null && _a !== void 0 ? _a : []);
        const len = nextTracks.length;
        // Find the last queued item in the current list and append the tracks.
        for (let i = 0; i < len; i++) {
            const track = nextTracks[i];
            if (track.uid === comparator.uid &&
                ((_b = track.metadata) === null || _b === void 0 ? void 0 : _b.iteration) === ((_c = comparator.metadata) === null || _c === void 0 ? void 0 : _c.iteration)) {
                nextTracks.splice(i, 1);
                break;
            }
            else {
                continue;
            }
        }
        if (nextTracks.length === len) {
            // Nothing was removed. Exit early.
            return Promise.resolve(false);
        }
        return this._setQueueNext(nextTracks, deviceId);
    }
}
//# sourceMappingURL=player_queue.js.map